import React, { forwardRef, useState, useEffect } from 'react';
import { cn } from '../../utils/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helper?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  variant?: 'default' | 'floating' | 'filled';
  inputSize?: 'sm' | 'default' | 'lg';
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    label, 
    error, 
    helper, 
    icon, 
    iconPosition = 'left',
    variant = 'default',
    inputSize = 'default',
    type = 'text', 
    ...props 
  }, ref) => {
    const [focused, setFocused] = useState(false);
    const [hasValue, setHasValue] = useState(!!props.value || !!props.defaultValue);
    
    useEffect(() => {
      setHasValue(!!props.value);
    }, [props.value]);
    
    const sizes = {
      sm: 'h-9 text-sm px-3',
      default: 'h-11 text-base px-4',
      lg: 'h-14 text-lg px-5',
    };
    
    const baseInputStyles = cn(
      "w-full font-medium transition-all duration-200",
      "bg-white dark:bg-surface-1",
      "border-2 rounded-xl",
      "placeholder:text-muted-foreground/60",
      "focus:outline-none focus:ring-0",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "file:border-0 file:bg-transparent file:text-sm file:font-medium",
      // Touch optimizations
      "touch:min-h-[48px]",
      sizes[inputSize],
      icon && iconPosition === 'left' && "pl-11",
      icon && iconPosition === 'right' && "pr-11",
    );
    
    const variants = {
      default: cn(
        baseInputStyles,
        "border-input hover:border-primary/50",
        "focus:border-primary focus:bg-white dark:focus:bg-surface-2",
        error && "border-destructive hover:border-destructive focus:border-destructive",
      ),
      floating: cn(
        baseInputStyles,
        "pt-6 pb-2",
        "border-input hover:border-primary/50",
        "focus:border-primary focus:bg-white dark:focus:bg-surface-2",
        error && "border-destructive hover:border-destructive focus:border-destructive",
      ),
      filled: cn(
        baseInputStyles,
        "border-transparent",
        "bg-gray-100 dark:bg-surface-2",
        "hover:bg-gray-200 dark:hover:bg-surface-3",
        "focus:bg-white dark:focus:bg-surface-1",
        "focus:border-primary focus:shadow-soft",
        error && "bg-destructive/10 hover:bg-destructive/20 focus:border-destructive",
      ),
    };
    
    const floatingLabel = variant === 'floating' && label;
    
    return (
      <div className="space-y-2">
        {label && variant !== 'floating' && (
          <label
            className="text-sm font-medium text-foreground/90"
            htmlFor={props.id}
          >
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className={cn(
              "absolute inset-y-0 flex items-center pointer-events-none text-muted-foreground",
              iconPosition === 'left' ? "left-3" : "right-3"
            )}>
              {icon}
            </div>
          )}
          <input
            type={type}
            className={cn(
              variants[variant],
              className
            )}
            ref={ref}
            onFocus={() => setFocused(true)}
            onBlur={(e) => {
              setFocused(false);
              setHasValue(!!e.target.value);
            }}
            onChange={(e) => {
              props.onChange?.(e);
              setHasValue(!!e.target.value);
            }}
            aria-invalid={!!error}
            aria-describedby={error ? `${props.id}-error` : helper ? `${props.id}-helper` : undefined}
            {...props}
          />
          {floatingLabel && (
            <label
              htmlFor={props.id}
              className={cn(
                "absolute left-4 transition-all duration-200 pointer-events-none",
                "text-muted-foreground",
                focused || hasValue
                  ? "top-2 text-xs font-medium text-primary"
                  : "top-4 text-base"
              )}
            >
              {label}
            </label>
          )}
        </div>
        {error && (
          <p id={`${props.id}-error`} className="text-sm text-destructive flex items-center gap-1" role="alert" aria-live="polite">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </p>
        )}
        {helper && !error && (
          <p id={`${props.id}-helper`} className="text-sm text-muted-foreground">
            {helper}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
